import { useImperativeHandle, useLayoutEffect, useMemo, useState } from "react";
import { Heading } from "@/components/ui/Heading";
import { DonationAmountProps } from "./DonationAmountProps";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import { RadioCard, RadioGroup } from "@/components/ui/Radio";
import classes from "./DonationAmount.module.scss";
import { useDonationFormStore, DonationType } from "@/stores";
import { Text } from "@/components/ui/Text";
import { formatCurrency } from "@/utils/formatters";
import type { AmountConfig } from "@/types/components/AmountComponent";
import { useTranslation } from "@/hooks/useTranslation";
import { camelCaseKeys } from "@/utils/camel-case-keys";
import { InputCurrency } from "@/components/ui/InputCurrency";
import { useAppConfig } from "@/hooks/useAppConfig";

const CUSTOM_AMOUNT = "custom" as const;

type AmountConfigFormatted = {
  monthlyDonationEnabled: AmountConfig["monthly-donation-enabled"];
  monthlyDonationAmounts: AmountConfig["monthly-donation-amounts"];
  monthlyDonationLabel: AmountConfig["monthly-donation-label"];
  monthlyDonationOther: AmountConfig["monthly-donation-other"];
  monthlyDonationOtherAmountDescription: AmountConfig["monthly-donation-other-amount-description"];
  oneOffDonationEnabled: AmountConfig["one-off-donation-enabled"];
  oneOffDonationLabel: AmountConfig["one-off-donation-label"];
  oneOffDonationOther: AmountConfig["one-off-donation-other"];
  oneOffDonationOtherAmountDescription: AmountConfig["one-off-donation-other-amount-description"];
  oneOffDonationAmounts: AmountConfig["one-off-donation-amounts"];
  amountIntro: AmountConfig["amount-intro"];
  oneOffDonationAmountMin: AmountConfig["one-off-donation-amount-min"];
  monthlyDonationAmountMin: AmountConfig["monthly-donation-amount-min"];
};

export function DonationAmount(props: DonationAmountProps) {
  const { config, className, ref } = props;

  const {
    monthlyDonationEnabled,
    monthlyDonationLabel,
    monthlyDonationOther,
    monthlyDonationAmounts,
    oneOffDonationAmounts,
    amountIntro,
    monthlyDonationOtherAmountDescription,
    oneOffDonationEnabled,
    oneOffDonationLabel,
    oneOffDonationOther,
    oneOffDonationOtherAmountDescription,
    oneOffDonationAmountMin,
    monthlyDonationAmountMin,
  } = camelCaseKeys(config) as unknown as AmountConfigFormatted;

  const { amountTitle } = useAppConfig();

  const [customAmount, setCustomAmount] = useState<string>("");
  const [monthlyCustomAmmountError, setMonthlyCustomAmmountError] = useState<
    string | null
  >(null);
  const [oneOffCustomAmmountError, setOneOffCustomAmmountError] = useState<
    string | null
  >(null);

  const {
    donationType,
    setDonationType,
    donationAmount,
    setDonationAmount,
    setHasCustomAmount,
    hasCustomAmount,
  } = useDonationFormStore();

  const t = useTranslation("amount");

  useImperativeHandle(ref, () => ({
    validate: async () => {
      if (donationType === DonationType.Monthly && monthlyDonationEnabled) {
        if (!donationAmount || donationAmount < monthlyDonationAmountMin) {
          const errorMessage = t("errors.amount-minimum", {
            minimum: formatCurrency(monthlyDonationAmountMin),
          }) as string;
          setMonthlyCustomAmmountError(errorMessage);

          return {
            isValid: false,
          };
        }
      }

      if (donationType === DonationType.OneOff && oneOffDonationEnabled) {
        if (!donationAmount || donationAmount < oneOffDonationAmountMin) {
          const errorMessage = t("errors.amount-minimum", {
            minimum: formatCurrency(oneOffDonationAmountMin),
          }) as string;
          setOneOffCustomAmmountError(errorMessage);

          return {
            isValid: false,
          };
        }
      }

      return {
        isValid: true,
      };
    },
  }));

  useLayoutEffect(() => {
    if (!oneOffDonationEnabled && monthlyDonationEnabled) {
      setDonationType(DonationType.Monthly);
    }

    if (!monthlyDonationEnabled && oneOffDonationEnabled) {
      setDonationType(DonationType.OneOff);
    }
  }, [oneOffDonationEnabled, monthlyDonationEnabled, setDonationType]);

  const resetDonation = () => {
    setDonationAmount(null);
    setCustomAmount("");
    setHasCustomAmount(false);
  };

  const handleChangeTab = (tabName: string) => {
    resetDonation();

    setDonationType(tabName as DonationType);
  };

  const clearErrors = () => {
    setMonthlyCustomAmmountError(null);
    setOneOffCustomAmmountError(null);
  };
  const handleChangeAmount = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    clearErrors();

    if (value === CUSTOM_AMOUNT) {
      setHasCustomAmount(true);
      setDonationAmount(null);
    } else {
      setHasCustomAmount(false);
      setCustomAmount("");
      setDonationAmount(Number(e.target.value));
    }
  };

  const handleChangeCustomAmount = (value: string) => {
    clearErrors();
    setCustomAmount(value);
    const customAmountParsed = Number.parseFloat(value);

    if (!Number.isNaN(customAmountParsed) && customAmountParsed >= 1) {
      setDonationAmount(customAmountParsed);
    } else {
      setDonationAmount(null);
    }
  };

  const amount = hasCustomAmount ? CUSTOM_AMOUNT : (donationAmount ?? "");

  const hasValidCustomAmount = useMemo(() => {
    if (donationType === DonationType.Monthly) {
      return (
        customAmount !== "" &&
        !monthlyCustomAmmountError &&
        parseFloat(customAmount) >= monthlyDonationAmountMin
      );
    }

    if (donationType === DonationType.OneOff) {
      return (
        customAmount !== "" &&
        !oneOffCustomAmmountError &&
        parseFloat(customAmount) >= oneOffDonationAmountMin
      );
    }

    return false;
  }, [
    customAmount,
    donationType,
    monthlyCustomAmmountError,
    monthlyDonationAmountMin,
    oneOffCustomAmmountError,
    oneOffDonationAmountMin,
  ]);

  const hasMultipleTypes = monthlyDonationEnabled && oneOffDonationEnabled;

  return (
    <div className={className}>
      <div className={classes.header}>
        <Heading as="h1" size="2xl" gutterBottom>
          {amountTitle}
        </Heading>
        <div className={classes.content}>{t(amountIntro.i18n)}</div>
      </div>

      <Tabs value={donationType ?? ""} onValueChange={handleChangeTab}>
        {hasMultipleTypes && (
          <TabsList>
            <TabsTrigger value={DonationType.OneOff}>
              {t(oneOffDonationLabel.i18n)}
            </TabsTrigger>

            <TabsTrigger value={DonationType.Monthly}>
              {t(monthlyDonationLabel.i18n)}
            </TabsTrigger>
          </TabsList>
        )}

        <TabsContent value={DonationType.Monthly}>
          <RadioGroup
            name="one-off-donation-amount"
            value={amount}
            onChange={handleChangeAmount}
            direction="row"
          >
            {monthlyDonationAmounts.map((amount) => (
              <RadioCard
                key={amount.amount}
                title={formatCurrency(amount.amount)}
                description={t(amount.description.i18n)}
                value={Number(amount.amount)}
                image={amount.image?.src}
                recommended={amount.recommended}
                recommendedText={t("recommended-amount") as string}
              />
            ))}

            {monthlyDonationOther && (
              <RadioCard
                disableSelectedStyle={!hasValidCustomAmount}
                value={CUSTOM_AMOUNT}
                ariaLabel="Enter own amount"
              >
                <div className={classes.customAmount}>
                  <div>
                    <Heading size="xl" gutterBottom>
                      Your own amount
                    </Heading>
                    <Text>{t(monthlyDonationOtherAmountDescription.i18n)}</Text>
                  </div>

                  <div className={classes.customAmountInput}>
                    <InputCurrency
                      min={monthlyDonationAmountMin}
                      value={customAmount}
                      onChange={handleChangeCustomAmount}
                      placeholder="0.00"
                      hasError={monthlyCustomAmmountError !== null}
                    />

                    {monthlyCustomAmmountError ? (
                      <Text
                        size="sm"
                        className={classes.error}
                        aria-live="polite"
                      >
                        {monthlyCustomAmmountError}
                      </Text>
                    ) : (
                      <Text size="sm">
                        {formatCurrency(monthlyDonationAmountMin)} minimum
                      </Text>
                    )}
                  </div>
                </div>
              </RadioCard>
            )}
          </RadioGroup>
        </TabsContent>

        <TabsContent value={DonationType.OneOff}>
          <RadioGroup
            direction="row"
            name="monthly-donation-amount"
            value={amount}
            onChange={handleChangeAmount}
          >
            {oneOffDonationAmounts.map((amount) => (
              <RadioCard
                key={amount.amount}
                title={formatCurrency(amount.amount)}
                description={t(amount.description.i18n)}
                value={Number(amount.amount)}
                image={amount.image?.src}
                recommended={amount.recommended}
                recommendedText={t("recommended-amount") as string}
              />
            ))}

            {oneOffDonationOther && (
              <RadioCard
                disableSelectedStyle={!hasValidCustomAmount}
                value={CUSTOM_AMOUNT}
                ariaLabel="Enter own amount"
              >
                <div className={classes.customAmount}>
                  <div>
                    <Heading size="xl" gutterBottom>
                      Your own amount
                    </Heading>
                    <Text>{t(oneOffDonationOtherAmountDescription.i18n)}</Text>
                  </div>

                  <div className={classes.customAmountInput}>
                    <InputCurrency
                      min={oneOffDonationAmountMin}
                      value={customAmount}
                      onChange={handleChangeCustomAmount}
                      placeholder="0.00"
                      hasError={oneOffCustomAmmountError !== null}
                    />

                    {oneOffCustomAmmountError ? (
                      <Text
                        size="sm"
                        className={classes.error}
                        aria-live="polite"
                      >
                        {oneOffCustomAmmountError}
                      </Text>
                    ) : (
                      <Text size="sm">
                        {formatCurrency(oneOffDonationAmountMin)} minimum
                      </Text>
                    )}
                  </div>
                </div>
              </RadioCard>
            )}
          </RadioGroup>
        </TabsContent>
      </Tabs>
    </div>
  );
}
